import type { SuggestionStatus, SupportMe, TicketStatus } from '@epc/contracts';
import { queryOptions } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';
import type { Tone } from '../ui/feedback';
import { api } from './api';

/** Состояние помощи человека: экскурсия пройдена, непрочитанные ответы (БЗ-03, БЗ-23) */
export const supportMeQuery = queryOptions({
  queryKey: ['support', 'me'],
  queryFn: () => api<SupportMe>('/support/me'),
  staleTime: 60_000,
  retry: false,
});

export const TICKET_TONE: Record<TicketStatus, Tone> = {
  new: 'accent',
  in_progress: 'info',
  waiting_user: 'warning',
  waiting_owner: 'warning',
  with_dev: 'neutral',
  answered: 'success',
  closed: 'neutral',
};

/** Что человек сообщает кнопкой «Сообщить»: ошибка и просьба о помощи — обращения, доработка — предложение */
export type ReportKind = 'bug' | 'question' | 'idea';

/**
 * Что открыто в оболочке портала: панель помощи, форма обращения или предложения, панель «Сообщить», экскурсия.
 * Открывают их кнопки «Сообщить» и «Помощь» в шапке и страница «Поддержка»; рисует их оболочка, чтобы форма была
 * доступна с любой страницы (БЗ-21, БЗ-28).
 */
interface SupportUi {
  help: boolean;
  dialog: 'ticket' | 'suggestion' | null;
  tour: boolean;
  /** Панель «Сообщить»: открыта ли и какой вид выбран при открытии (null — оставить прежний) */
  report: boolean;
  reportKind: ReportKind | null;
}

let ui: SupportUi = { help: false, dialog: null, tour: false, report: false, reportKind: null };
const listeners = new Set<() => void>();

function set(next: Partial<SupportUi>) {
  ui = { ...ui, ...next };
  for (const l of listeners) l();
}

export const supportUi = {
  openHelp: () => set({ help: true }),
  closeHelp: () => set({ help: false }),
  openDialog: (kind: 'ticket' | 'suggestion') => set({ dialog: kind, help: false }),
  closeDialog: () => set({ dialog: null }),
  openTour: () => set({ tour: true, help: false }),
  closeTour: () => set({ tour: false }),
  /** «Сообщить» с любой страницы: ошибка, нужна помощь, доработка; страница и снимок экрана прикладываются сами */
  openReport: (kind?: ReportKind) =>
    set({ report: true, reportKind: kind ?? null, help: false, dialog: null }),
  closeReport: () => set({ report: false, reportKind: null }),
};

export function useSupportUi(): SupportUi {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => ui,
    () => ui,
  );
}

/** Цвет статуса предложения в списках, анализе и карточке (БЗ-29) */
export const SUGGESTION_TONE: Record<SuggestionStatus, 'neutral' | 'info' | 'accent' | 'success' | 'danger'> =
  {
    review: 'neutral',
    planned: 'info',
    in_progress: 'accent',
    released: 'success',
    rejected: 'danger',
  };
